import React, { useState } from 'react';
import { Phone, Loader2, MessageCircle } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useIntegrationFeatures } from '@/hooks/useIntegrationFeatures';
import { integrationService } from '@/api/integrations/integrationService';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';

export interface Click2CallTriggerProps {
    /** Phone number to display and to call when confirmed */
    value: string;
    /** Optional customer id to pass to the call API */
    customerId?: string;
    /** Placeholder when value is empty */
    placeholder?: string;
    className?: string;
    /** When false, still show value but do not allow click-to-call (overrides integration-based enabled state) */
    disabled?: boolean;
    /** 'full' = value + phone icon (default). 'buttonOnly' = just the phone icon button, for use next to InlineEdit */
    displayMode?: 'full' | 'buttonOnly';
}

/**
 * General click-to-call trigger component (similar to InlineEdit).
 * Shows a phone value and a phone icon; when enabled and clicked, user confirms then the call is initiated.
 * Enabled when VOIP integration is active and (provider is not Arestik/VoiceFlow C2C, or user has extension set).
 */
export const Click2CallTrigger: React.FC<Click2CallTriggerProps> = ({
    value,
    customerId,
    placeholder = 'No number',
    className,
    disabled = false,
    displayMode = 'full',
}) => {
    const { isClickToCallEnabled, isLoading } = useIntegrationFeatures();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [calling, setCalling] = useState(false);

    const enabled = !disabled && isClickToCallEnabled && Boolean(value?.trim());

    const handleTriggerClick = () => {
        if (!enabled || !value?.trim()) return;
        setConfirmOpen(true);
    };

    const handleConfirmCall = async () => {
        if (!value?.trim()) return;
        try {
            setCalling(true);
            const response = await integrationService.initiateCall({
                phone_number: value.trim(),
                customer_id: customerId,
            });
            if (response.success) {
                toast.success(`Calling ${value.trim()}...`);
                setConfirmOpen(false);
            } else {
                toast.error(response.message || 'Failed to initiate call');
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to initiate call';
            toast.error(message);
        } finally {
            setCalling(false);
        }
    };

    const displayValue = value?.trim() || placeholder;

    /** Format phone for WhatsApp: digits only (country code + number) */
    const whatsappHref = value?.trim() ? `https://wa.me/${value.replace(/\D/g, '')}` : undefined;

    const triggerContent =
        displayMode === 'buttonOnly' ? (
            <span className="inline-flex items-center gap-0.5">
                {isClickToCallEnabled && value?.trim() ? (
                    <button
                        type="button"
                        onClick={e => {
                            e.stopPropagation();
                            handleTriggerClick();
                        }}
                        className={cn(
                            'inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
                            className
                        )}
                        title="Click to call"
                        aria-label={`Call ${displayValue}`}
                    >
                        <Phone className="h-4 w-4" />
                    </button>
                ) : null}
                {value?.trim() && (
                    <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className={cn(
                            'inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
                            className
                        )}
                        title="Open WhatsApp"
                        aria-label={`Open WhatsApp chat with ${displayValue}`}
                    >
                        <MessageCircle className="h-4 w-4" />
                    </a>
                )}
            </span>
        ) : (
            <div
                className={cn(
                    'group flex items-center justify-between min-w-0 rounded px-2 py-1 -mx-2 transition-colors',
                    enabled && 'cursor-pointer hover:bg-muted/50',
                    className
                )}
                onClick={enabled ? handleTriggerClick : undefined}
                role={enabled ? 'button' : undefined}
                aria-label={enabled ? `Call ${displayValue}` : undefined}
            >
                <span
                    className={cn(
                        'truncate flex-1 mr-2 font-mono',
                        !value?.trim() && 'text-muted-foreground'
                    )}
                >
                    {displayValue}
                </span>
                <span className="flex items-center gap-0.5 flex-shrink-0">
                    {isLoading ? (
                        <Loader2 className="h-3 w-3 text-muted-foreground animate-spin" />
                    ) : isClickToCallEnabled ? (
                        <Phone
                            className={cn(
                                'h-3 w-3',
                                enabled ? 'text-muted-foreground' : 'text-muted-foreground/50'
                            )}
                        />
                    ) : null}
                    {value?.trim() && (
                        <a
                            href={whatsappHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            title="Open WhatsApp"
                            aria-label={`Open WhatsApp chat with ${displayValue}`}
                        >
                            <MessageCircle className="h-3 w-3" />
                        </a>
                    )}
                </span>
            </div>
        );

    return (
        <>
            {triggerContent}

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Call this number?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You are about to call{' '}
                            <strong className="font-mono">{value?.trim()}</strong>. Your phone will
                            ring first; after you answer, the other party will be dialed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={calling}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={e => {
                                e.preventDefault();
                                handleConfirmCall();
                            }}
                            disabled={calling}
                        >
                            {calling ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Calling…
                                </>
                            ) : (
                                'Call'
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
